import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';

type ErrorLike = {
  digest?: unknown;
  message?: unknown;
  status?: unknown;
  statusCode?: unknown;
};

export function isExpectedNotFound(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const candidate = error as ErrorLike;
  return (
    candidate.digest === 'NEXT_NOT_FOUND' ||
    (typeof candidate.digest === 'string' &&
      candidate.digest.startsWith('NEXT_HTTP_ERROR_FALLBACK;404')) ||
    (typeof candidate.message === 'string' &&
      candidate.message.startsWith('NEXT_HTTP_ERROR_FALLBACK;404')) ||
    candidate.status === 404 ||
    candidate.statusCode === 404
  );
}

export function sanitizeClientEvent(event: ErrorEvent): ErrorEvent {
  const { breadcrumbs: _breadcrumbs, user: _user, ...withoutSensitiveData } = event;
  if (!event.request) return withoutSensitiveData;

  const url = event.request.url?.replace(/[?#].*$/, '');
  return {
    ...withoutSensitiveData,
    request: url ? { url } : {},
  };
}

export function initializeClientErrorTracking(): void {
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

  Sentry.init({
    dsn,
    enabled: Boolean(dsn),
    environment: process.env.NODE_ENV,
    sendDefaultPii: false,
    maxBreadcrumbs: 0,
    tracesSampleRate: 0,
    beforeSend(event, hint) {
      return isExpectedNotFound(hint.originalException) ? null : sanitizeClientEvent(event);
    },
  });
}

export function captureClientException(error: unknown): void {
  if (!isExpectedNotFound(error)) Sentry.captureException(error);
}
